import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getListCustomPagesQueryKey,
  useGetNavigationLayouts,
  useListCustomPages
} from '@cdf/api-client-react';
import {
  ChevronDownIcon,
  ExternalLinkIcon,
  GripVerticalIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  Settings2Icon,
} from 'lucide-react';

import { FeatureStageBadge, featureStageLabels } from '@/components/features/feature-stage-badge';
import { Button } from '@/components/ui/button';
import { ScreenSearch } from '@/components/layout/screen-search';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { filterNavigationSections, navigationResultCount } from '@/lib/navigation-search';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { TenderNavigationBadge } from '@/components/tenders/tender-opportunity-unread';
import {
  getAdminNavigation,
  getMainNavigation,
  type NavItem,
  type NavSection
} from '@/lib/navigation';
import { resolveNavigation } from '@/lib/navigation-layout';
import {
  isPinnableNavItem,
  resolveFavouriteItems,
  useNavigationFavourites
} from '@/lib/navigation-favourites';
import {
  FavouriteActions,
  NavigationFavouritesContext,
  PinToggle
} from '@/components/layout/sidebar-favourites';
import { getNavigationLayoutsQueryKey } from '@/lib/navigation-layout-query';
import { useWorkspace } from '@/components/workspace/workspace-provider';
import { usePublicMicrositeLink } from '@/hooks/use-public-microsite-link';
import { useManageMode } from '@/components/manage/manage-mode';
import { DESKTOP_MEDIA_QUERY } from '@/lib/device-viewports';
import { cn } from '@/lib/utils';

const GROUPS_STORAGE_KEY = 'cdf-sidebar-open-groups';

/** Read the persisted open/closed state of expandable sidebar groups. */
function readOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function persistOpenGroup(title: string, open: boolean) {
  try {
    const groups = readOpenGroups();
    groups[title] = open;
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  } catch {
    // The current session still keeps the selected state if storage is unavailable.
  }
}

function useSidebarLabel(item: NavItem, collapsed: boolean) {
  const { snapshot } = useTenantEntity();
  if (!collapsed) return undefined;
  const stage = item.moduleKey ? snapshot?.featureStages?.[item.moduleKey] : undefined;
  return stage === 'preview' || stage === 'deprecated'
    ? `${item.title}, ${featureStageLabels[stage]} feature`
    : item.title;
}

function SidebarFeatureStage({ moduleKey, collapsed }: { moduleKey?: string; collapsed: boolean }) {
  const { snapshot } = useTenantEntity();
  return <FeatureStageBadge
    stage={moduleKey ? snapshot?.featureStages?.[moduleKey] : undefined}
    compact
    className={collapsed ? 'absolute right-0 top-0 size-3 text-[9px]' : 'ml-auto'}
  />;
}

const linkClasses = ({ isActive }: { isActive: boolean }) =>
  cn(
    'focus-visible:ring-ring relative flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
    isActive
      ? 'bg-accent text-accent-foreground font-medium'
      : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground'
  );

/** Room for the trailing pin/manage button, which grows to 44px on touch screens. */
const trailingActionPadding = 'pr-8 [@media(pointer:coarse)]:pr-12';

function PublicMicrositeSidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { url, isPublished, isLoading } = usePublicMicrositeLink();
  const accessibleLabel = useSidebarLabel(item, collapsed);
  const unavailableTitle = isLoading ? 'Checking publication status…' : 'Microsite not published yet';
  const link = isPublished && url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={accessibleLabel}
      className={linkClasses({ isActive: false })}
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
      <SidebarFeatureStage moduleKey={item.moduleKey} collapsed={collapsed} />
      {!collapsed && (
        <ExternalLinkIcon aria-hidden className="text-muted-foreground ml-auto size-3 shrink-0" />
      )}
      <span className="sr-only">(opens in new tab)</span>
    </a>
  ) : (
    <span
      aria-disabled="true"
      aria-label={accessibleLabel}
      title={unavailableTitle}
      className={cn(
        linkClasses({ isActive: false }),
        'text-muted-foreground/60 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground/60'
      )}
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
      <SidebarFeatureStage moduleKey={item.moduleKey} collapsed={collapsed} />
      {!collapsed && <ExternalLinkIcon aria-hidden className="ml-auto size-3 shrink-0" />}
    </span>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{isPublished ? item.title : unavailableTitle}</TooltipContent>
    </Tooltip>
  );
}

/** The Tenders/Proposals entry carries the unread tender opportunity badge. */
function isTenderNavigation(to?: string) {
  const path = to?.split('?')[0];
  return path === '/manage/proposals' || path === '/manage/tenders';
}

type FavouritePosition = { previousPath?: string; nextPath?: string };

function SidebarLink({
  item,
  collapsed,
  favourite
}: {
  item: NavItem;
  collapsed: boolean;
  /** Set for rows in the Favourites section: shows manage actions instead of the pin toggle. */
  favourite?: FavouritePosition;
}) {
  const location = useLocation();
  const accessibleLabel = useSidebarLabel(item, collapsed);
  const pinPath = !collapsed && isPinnableNavItem(item) ? item.to : null;
  if (item.dynamic === 'public-microsite') {
    return <PublicMicrositeSidebarLink item={item} collapsed={collapsed} />;
  }
  if (!item.to && !item.href) return null;
  const itemPath = item.to?.split('?')[0];
  const pathMatches = itemPath === '/'
    ? location.pathname === '/'
    : Boolean(itemPath && (location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`)));
  const tabMatches = item.to?.includes('?') ? location.search === item.to.slice(item.to.indexOf('?')) : true;
  const active = pathMatches && tabMatches;

  const link = item.to ? (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      aria-label={accessibleLabel}
      aria-current={active ? 'page' : undefined}
      draggable={favourite ? false : undefined}
      className={cn(linkClasses({ isActive: active }), isTenderNavigation(item.to) && 'relative', pinPath && trailingActionPadding)}
    >
      {favourite && !collapsed ? (
        <>
          <item.icon aria-hidden className="size-4 shrink-0 group-hover/nav:hidden" />
          <GripVerticalIcon
            aria-hidden
            data-testid="favourite-drag-handle"
            className="hidden size-4 shrink-0 cursor-grab group-hover/nav:block"
          />
        </>
      ) : (
        <item.icon aria-hidden className="size-4 shrink-0" />
      )}
      {!collapsed && <span className="truncate">{item.title}</span>}
      <SidebarFeatureStage moduleKey={item.moduleKey} collapsed={collapsed} />
      {isTenderNavigation(item.to) && <TenderNavigationBadge collapsed={collapsed} />}
    </NavLink>
  ) : (
    <a
      href={item.href}
      target={item.external ? '_blank' : undefined}
      rel={item.external ? 'noreferrer' : undefined}
      aria-label={accessibleLabel}
      className={linkClasses({ isActive: false })}
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
      <SidebarFeatureStage moduleKey={item.moduleKey} collapsed={collapsed} />
      {!collapsed && item.external && (
        <ExternalLinkIcon aria-hidden className="text-muted-foreground ml-auto size-3 shrink-0" />
      )}
      {item.external && <span className="sr-only">(opens in new tab)</span>}
    </a>
  );

  if (!collapsed) {
    if (!pinPath) return link;
    return (
      <div className="group/nav relative">
        {link}
        {favourite ? (
          <FavouriteActions
            path={pinPath}
            label={item.title}
            previousPath={favourite.previousPath}
            nextPath={favourite.nextPath}
          />
        ) : (
          <PinToggle path={pinPath} title={item.title} />
        )}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{accessibleLabel ?? item.title}</TooltipContent>
    </Tooltip>
  );
}

function SidebarGroup({
  item,
  collapsed,
  forceOpen = false
}: {
  item: NavItem;
  collapsed: boolean;
  forceOpen?: boolean;
}) {
  const location = useLocation();
  const accessibleLabel = useSidebarLabel(item, collapsed);
  const children = item.children ?? [];
  const childActive = children.some((child) => location.pathname === child.to);
  const [open, setOpen] = React.useState(
    () => readOpenGroups()[item.title] ?? childActive
  );

  React.useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const toggleOpen = () => {
    setOpen((value) => {
      persistOpenGroup(item.title, !value);
      return !value;
    });
  };

  // Collapsed rail: expose sub items via a flyout menu instead of inline expansion.
  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={accessibleLabel}
            className={cn(
              'focus-visible:ring-ring relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
              childActive
                ? 'bg-accent text-accent-foreground font-medium'
                : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground'
            )}
          >
            <item.icon aria-hidden className="size-4 shrink-0" />
            <SidebarFeatureStage moduleKey={item.moduleKey} collapsed />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
          {children.map((child) => (
            <DropdownMenuItem key={child.to} asChild>
              <NavLink to={child.to}>{child.title}<SidebarFeatureStage moduleKey={item.moduleKey} collapsed={false} />{isTenderNavigation(child.to) && <TenderNavigationBadge />}</NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open || forceOpen}
        className={cn(
          'focus-visible:ring-ring relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
          childActive
            ? 'text-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground'
        )}
      >
        <item.icon aria-hidden className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{item.title}</span>
        <SidebarFeatureStage moduleKey={item.moduleKey} collapsed={false} />
        <ChevronDownIcon
          aria-hidden
          className={cn(
            'size-3.5 transition-transform motion-reduce:transition-none',
            open ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
      {(open || forceOpen) && (
        <div className="border-border/60 mt-0.5 ml-4 flex flex-col gap-0.5 border-l pl-3">
          {children.map((child) => (
            <div key={child.to} className="group/nav relative">
              <NavLink
                to={child.to}
                end
                className={({ isActive }) =>
                  cn(
                    'focus-visible:ring-ring block truncate rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                    trailingActionPadding,
                    isActive
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground'
                  )
                }
              >
                {child.title}<SidebarFeatureStage moduleKey={item.moduleKey} collapsed={false} />
                {isTenderNavigation(child.to) && <TenderNavigationBadge />}
              </NavLink>
              <PinToggle path={child.to} title={child.title} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Favourites only move up and down within their list. */
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 });

function SortableFavourite({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      title="Drag to reorder"
      className={cn('relative touch-manipulation', isDragging && 'z-10 cursor-grabbing opacity-80 shadow-sm')}
    >
      {children}
    </div>
  );
}

/** Favourites rows can be dragged to reorder; Move up / Move down stay available from each row's menu. */
function SortableFavourites({ items, moduleKey }: { items: NavItem[]; moduleKey?: string }) {
  const favourites = React.useContext(NavigationFavouritesContext);
  const dragging = React.useRef(false);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );
  const ids = items.flatMap(item => (item.to ? [item.to] : []));
  const endDrag = () => {
    // The click that ends a drag must not also open the dropped screen.
    window.setTimeout(() => { dragging.current = false; }, 0);
  };
  const onDragEnd = (event: DragEndEvent) => {
    endDrag();
    const { active, over } = event;
    if (over && active.id !== over.id) favourites?.move(String(active.id), String(over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[verticalOnly]}
      onDragStart={() => { dragging.current = true; }}
      onDragCancel={endDrag}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div
          className="flex flex-col gap-0.5"
          onClickCapture={(event) => {
            if (dragging.current) { event.preventDefault(); event.stopPropagation(); }
          }}
        >
          {items.map((item, index) => (
            <SortableFavourite key={item.to ?? item.title} id={item.to ?? item.title}>
              <SidebarLink
                item={{ ...item, moduleKey: item.moduleKey ?? moduleKey }}
                collapsed={false}
                favourite={{ previousPath: items[index - 1]?.to, nextPath: items[index + 1]?.to }}
              />
            </SortableFavourite>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SidebarSection({
  section,
  collapsed,
  forceOpen = false,
  variant
}: {
  section: NavSection;
  collapsed: boolean;
  forceOpen?: boolean;
  /** The Favourites section lets people rename, reorder and remove its entries. */
  variant?: 'favourites';
}) {
  const location = useLocation();
  const storageKey = `section:${section.heading}`;
  const active = section.items.some((item) => {
    if (item.children?.some((child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`))) {
      return true;
    }
    if (!item.to) return false;
    return item.to === '/' ? location.pathname === '/' : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  });
  const [open, setOpen] = React.useState(() => readOpenGroups()[storageKey] ?? true);
  const contentId = React.useId();

  React.useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const toggleOpen = () => {
    setOpen((value) => {
      const next = !value;
      persistOpenGroup(storageKey, next);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-0.5">
      {!collapsed && (
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open || forceOpen}
          aria-controls={contentId}
          className="text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground focus-visible:ring-ring flex w-full items-center justify-between gap-2 rounded-md px-2 pt-2 pb-1 text-left text-[11px] font-medium tracking-wider uppercase transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <span className="min-w-0 truncate">{section.heading}</span>
          <ChevronDownIcon
            aria-hidden
            className={cn('size-3.5 transition-transform motion-reduce:transition-none', open && 'rotate-180')}
          />
        </button>
      )}
      {(collapsed || open || forceOpen) && variant === 'favourites' && !collapsed && (
        <div id={contentId}>
          <SortableFavourites items={section.items} moduleKey={section.moduleKey} />
        </div>
      )}
      {(collapsed || open || forceOpen) && !(variant === 'favourites' && !collapsed) && (
        <div id={contentId} className="flex flex-col gap-0.5">
          {section.items.map((item, index) =>
            item.children ? (
              <SidebarGroup
                key={item.key ?? item.to ?? item.href ?? item.title}
                item={{ ...item, moduleKey: item.moduleKey ?? section.moduleKey }}
                collapsed={collapsed}
                forceOpen={forceOpen}
              />
            ) : (
              <SidebarLink
                key={item.key ?? item.to ?? item.href ?? item.title}
                item={{ ...item, moduleKey: item.moduleKey ?? section.moduleKey }}
                collapsed={collapsed}
                favourite={variant === 'favourites'
                  ? { previousPath: section.items[index - 1]?.to, nextPath: section.items[index + 1]?.to }
                  : undefined}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

/** Inner sidebar panel — shared by desktop inline and mobile overlay. */
function SidebarPanel({
  collapsed,
  onToggle,
  onClose,
  navigationQuery,
  setNavigationQuery
}: {
  collapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
  navigationQuery: string;
  setNavigationQuery: (value: string) => void;
}) {
  const { hasPermission, platformPermissions, status, tenants, selected, enabledModules } = useTenantEntity();
  const manage = useManageMode();
  const { mode } = useWorkspace();
  const favourites = useNavigationFavourites();
  const sidebarHasPermission = (key: string) => (
    (key.startsWith('platform.') && platformPermissions.includes(key)) ||
    (key.startsWith('tenant.')
      ? (selected ? tenants.find(tenant => tenant.id === selected.tenantId)?.permissions?.includes(key) ?? hasPermission(key)
        : tenants.some(tenant => tenant.permissions?.includes(key)))
      : hasPermission(key))
  );
  const adminNavigation = ['ready', 'noAccess'].includes(status)
    ? getAdminNavigation(sidebarHasPermission, platformPermissions) : [];
  const customPagesQuery = useListCustomPages({ query: { queryKey: getListCustomPagesQueryKey(), enabled: status === 'ready' } });
  const layoutsQuery = useGetNavigationLayouts({
    query: {
      queryKey: getNavigationLayoutsQueryKey(),
      enabled: status === 'ready',
      staleTime: 5 * 60 * 1000
    }
  });
  const layout = layoutsQuery.data?.layouts[mode] ?? null;
  const resolvedNavigation = status === 'ready' ? resolveNavigation({
    view: mode,
    standardSections: getMainNavigation(mode),
    adminSections: [],
    customPages: customPagesQuery.data ?? [],
    layout,
    hasPermission: sidebarHasPermission,
    enabledModules
  }) : [];
  // Administration is fixed, permission-gated navigation, independent of saved work menus.
  resolvedNavigation.forEach(section => {
    section.items = section.items.filter(item => !item.to?.startsWith('/admin/'));
  });
  resolvedNavigation.push(...adminNavigation.map(section => ({
    ...section, id: `admin:${section.heading}`, placement: 'admin' as const
  })));
  // Favourites only show screens that are still in this person's menu.
  const favouritesSection: NavSection = {
    heading: 'Favourites',
    items: resolveFavouriteItems(resolvedNavigation, favourites.items)
  };
  const normalizedNavigationQuery = navigationQuery.trim();
  const filteredNavigation = React.useMemo(
    () => filterNavigationSections(resolvedNavigation, normalizedNavigationQuery),
    [resolvedNavigation, normalizedNavigationQuery]
  );
  const topSections = filteredNavigation.filter((section) => section.placement === 'top');
  const resourceSections = filteredNavigation.filter((section) => section.placement === 'resources');
  const adminSections = filteredNavigation.filter((section) => section.placement === 'admin');
  const resultCount = navigationResultCount(filteredNavigation);

  return (
    <NavigationFavouritesContext.Provider value={favourites}>
      {/* Collapse / close toggle */}
      <div
        className={cn(
          'flex items-center px-2 pt-2',
          collapsed ? 'justify-center px-0' : 'justify-end'
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose ?? onToggle}
              aria-label={onClose ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="shrink-0"
            >
              {onClose ? (
                <PanelLeftCloseIcon className="size-4" />
              ) : collapsed ? (
                <PanelLeftOpenIcon className="size-4" />
              ) : (
                <PanelLeftCloseIcon className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {onClose ? 'Close' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </TooltipContent>
        </Tooltip>
      </div>

      {manage.enabled && hasPermission('entity.manage') && (
        <div className="px-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size={collapsed ? 'icon' : 'sm'}
            className={cn(collapsed ? 'w-full' : 'w-full justify-start')}
            onClick={() => manage.open({ kind: 'navigation' })}
            aria-label="Manage navigation"
          >
            <Settings2Icon className="size-4" aria-hidden />
            {!collapsed && 'Manage menu'}
          </Button>
        </div>
      )}

      {!collapsed && <ScreenSearch value={navigationQuery} onChange={setNavigationQuery} count={resultCount} />}

      {/* User navigation */}
      <nav aria-label="Main" className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
        {/* Pinned favourites sit above Home; screen search already lists every match. */}
        {!normalizedNavigationQuery && favouritesSection.items.length > 0 && (
          <SidebarSection section={favouritesSection} collapsed={collapsed} variant="favourites" />
        )}

        {topSections.map((section) => (
          <SidebarSection key={section.id} section={section} collapsed={collapsed} forceOpen={Boolean(normalizedNavigationQuery)} />
        ))}

        {/* Bottom-aligned area: Resources, then admin settings */}
        <div className={cn('flex flex-col gap-2', !normalizedNavigationQuery && 'mt-auto')}>
          {resourceSections.map((section) => (
            <SidebarSection key={section.id} section={section} collapsed={collapsed} forceOpen={Boolean(normalizedNavigationQuery)} />
          ))}

          {/* Administration area — separated from user menus and permission-gated. */}
          {adminSections.length > 0 && (
            <div className="flex flex-col gap-0.5 border-border border-t pt-2">
              {adminSections.flatMap((section) => section.items.map((item) => (
                <SidebarLink
                  key={item.key ?? item.to ?? item.href ?? item.title}
                  item={item}
                  collapsed={collapsed}
                />
              )))}
            </div>
          )}
        </div>
      </nav>
    </NavigationFavouritesContext.Provider>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const { hasPermission, selected, status } = useTenantEntity();
  const manage = useManageMode();
  const [navigationQuery, setNavigationQuery] = React.useState('');
  React.useEffect(() => { setNavigationQuery(''); }, [selected?.tenantId, selected?.entityId, status]);
  const mobileTrigger = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const closeAtDesktop = () => { if (media.matches) onMobileClose(); };
    media.addEventListener('change', closeAtDesktop);
    return () => media.removeEventListener('change', closeAtDesktop);
  }, [onMobileClose]);
  // Close mobile sidebar on route change
  const location = useLocation();
  const currentPath = location.pathname + location.search;
  const prevPathRef = React.useRef(currentPath);
  React.useEffect(() => {
    if (currentPath !== prevPathRef.current) {
      prevPathRef.current = currentPath;
      onMobileClose();
    }
  }, [currentPath, onMobileClose]);

  React.useEffect(() => {
    if (manage.enabled && hasPermission('entity.manage') && collapsed) onToggle();
  }, [collapsed, hasPermission, manage.enabled, onToggle]);

  return (
    <>
      {/* ── Desktop inline panel ── */}
      <aside
        data-collapsed={collapsed}
        className={cn(
          'bg-sidebar text-sidebar-foreground border-border hidden h-full shrink-0 flex-col border-r transition-[width] duration-200 motion-reduce:transition-none md:flex',
          collapsed ? 'w-14' : 'w-60'
        )}
      >
        <SidebarPanel collapsed={collapsed} onToggle={onToggle} navigationQuery={navigationQuery} setNavigationQuery={setNavigationQuery} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={open => { if (!open) onMobileClose(); }}>
        <SheetContent side="left" showCloseButton={false} className="bg-sidebar text-sidebar-foreground flex flex-col md:hidden"
          overlayClassName="md:hidden"
          onEscapeKeyDown={event => {
            if (navigationQuery.trim()) { event.preventDefault(); setNavigationQuery(''); }
          }}
          onOpenAutoFocus={() => { mobileTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }}
          onCloseAutoFocus={event => { event.preventDefault(); mobileTrigger.current?.focus(); }}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Find a screen or choose a destination from your menu.</SheetDescription>
          <SidebarPanel collapsed={false} onClose={onMobileClose} onToggle={onToggle} navigationQuery={navigationQuery} setNavigationQuery={setNavigationQuery} />
        </SheetContent>
      </Sheet>
    </>
  );
}
